import _ from "lodash";
window._ = _;

import { createApp } from "vue";
import mitt from "mitt";
import SvgVue from "./components/SvgVue.vue";
import VueSplide from "@splidejs/vue-splide";
import VueGtag from "vue-gtag-next";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import { ResizeObserver as Polyfill } from "@juggle/resize-observer";

const emitter = mitt();

if (typeof window !== "undefined") {
    window.ResizeObserver = window.ResizeObserver || Polyfill;
}

/**
 * Vue Components
 */

// Universal
import AOneClick from "./components/AOneClick";
import StatusMessage from "./components/StatusMessage";
import Tooltip from "./components/Tooltip";

// Addresses
import AddressForm from "./components/addresses/AddressForm";

// Auth
import LoginForm from "./components/auth/LoginForm.vue";
import RegisterForm from "./components/auth/RegisterForm.vue";
import PasswordResetRequestForm from "./components/auth/PasswordResetRequestForm.vue";
import PasswordResetForm from "./components/auth/PasswordResetForm.vue";
import LoginLinkRequestForm from "./components/auth/LoginLinkRequestForm.vue";

// Cart
import Cart from "./components/cart/Cart";

// Charts
import ElevenzymeClinicalStudyChart from "./components/charts/ElevenzymeClinicalStudyChart";
import RedicalmClinicalStudyChart from "./components/charts/RedicalmClinicalStudyChart";
import RediniteClinicalStudyChart from "./components/charts/RediniteClinicalStudyChart";
import RedimindClinicalStudyChart from "./components/charts/RedimindClinicalStudyChart";
import RedimoveClinicalStudyChart from "./components/charts/RedimoveClinicalStudyChart";
import ProstacorClinicalStudyChart from "./components/charts/ProstacorClinicalStudyChart";
import TestocorClinicalStudyChart from "./components/charts/TestocorClinicalStudyChart";
import ThyrogardClinicalStudyChart from "./components/charts/ThyrogardClinicalStudyChart";
import HepagardClinicalStudyChart from "./components/charts/HepagardClinicalStudyChart";
import MenovairClinicalStudyChart from "./components/charts/MenovairClinicalStudyChart";
import ThreeBiomeClinicalStudyChart from "./components/charts/ThreeBiomeClinicalStudyChart";
import GlucosamineStudyChart from "./components/charts/GlucosamineStudyChart";
import HormoneDeclineStudyChart from "./components/charts/HormoneDeclineStudyChart";

// Checkout
import UserCheckout from "./components/checkout/UserCheckout";
import PaypalCheckout from "./components/checkout/PaypalCheckout";
import GuestCheckout from "./components/checkout/GuestCheckout";
import CheckoutShippingOptionButtons from "./components/checkout/CheckoutShippingOptionButtons";
import TermsAndConditionsButton from "./components/checkout/TermsAndConditionsButton";
import PostCheckoutEmailForm from "./components/checkout/PostCheckoutCompleteShippingAddress";
import PostCheckoutMap from "./components/checkout/PostCheckoutMap";
import HealthProfileSideBar from "./components/checkout/HealthProfileSideBar";

// Contact Us
import CompanyPhoneLink from "./components/contactUs/CompanyPhoneLink";

// Dashboard
import PrintMasterCartonLabelButton from "./components/dashboard/PrintMasterCartonLabelButton.vue";
import PrintUspsScanFormButton from "./components/dashboard/PrintUspsScanFormButton";
import SalesLeaderboard from "./components/dashboard/SalesLeaderboard";
import SalesTotals from "./components/dashboard/SalesTotals";

// Health Profile
import HealthProfileAddToCartButton from "./components/healthProfile/HealthProfileAddToCartButton";

// Mail Campaigns
import MailCampaigns from "./components/mailCampaigns/MailCampaigns";

// Navigation
import NavBar from "./components/nav/NavBar.vue";
import CartSideBar from "./components/nav/CartSideBar.vue";

// Orders
import AddressDropdown from "./components/orders/AddressDropdown";
import OrderSearchForm from "./components/orders/OrderSearchForm";
import ChangeOrderShippingAddressButton from "./components/orders/ChangeOrderShippingAddressButton";
import CancelOrderButton from "./components/orders/CancelOrderButton";
import FlagOrderAsFraudButton from "./components/orders/FlagOrderAsFraudButton";
import RefundOrderButton from "./components/orders/RefundOrderButton";
import RecordChargebackButton from "./components/orders/RecordChargebackButton.vue";
import ReverseChargebackButton from "./components/orders/ReverseChargebackButton.vue";
import ShipOrderButton from "./components/orders/ShipOrderButton";
import MarkOrderAsShippedButton from "./components/orders/MarkOrderAsShippedButton";
import EditOrderItemButton from "./components/orders/EditOrderItemButton";
import EditOrderShippingTimeButton from "./components/orders/EditOrderShippingTimeButton";
import EditOrderPlacedByUserButton from "./components/orders/EditOrderPlacedByUserButton";
import EditAddressFieldButton from "./components/orders/EditAddressFieldButton.vue";
import ShipmentStatusButton from "./components/orders/ShipmentStatusButton";
import CombineOrderButton from "./components/orders/CombineOrderButton";
import UncombineOrderButton from "./components/orders/UncombineOrderButton";
import PreviousOrderSummaryButton from "./components/orders/PreviousOrderSummaryButton";

// Payment Methods
import PaymentMethodIcon from "./components/paymentMethods/PaymentMethodIcon";
import PaymentMethodForm from "./components/paymentMethods/PaymentMethodForm";

// Products
import BreadcrumbPhoneNumber from "./components/products/BreadcrumbPhoneNumber";
import PhoneNumber from "./components/products/PhoneNumber";
import ProductVideoModal from "./components/products/ProductVideoModal";
import ProductImageGallery from "./components/products/ProductImageGallery";
import ProductIngredientsBreakdown from "./components/products/ProductIngredientsBreakdown";
import ProductOrderForm from "./components/products/ProductOrderForm";
import OutOfStockForm from "./components/products/OutOfStockForm";
import ProductQuantityAndSubscriptionOptionButtons from "./components/products/ProductQuantityAndSubscriptionOptionButtons";
import RecommendedProductSideBar from "./components/products/RecommendedProductSideBar";
import RedicalmCycleOfNegativeBehavior from "./components/products/RedicalmCycleOfNegativeBehavior";
import CycleStages from "./components/products/CycleStages";
import NextCycleStage from "./components/products/NextCycleStage";
import BuyWithPrimeButton from "./components/products/BuyWithPrimeButton";
import AddToCartButton from "./components/products/AddToCartButton";

// Product Categories
import ProductCategoryHeading from "./components/productCategories/ProductCategoryHeading";
import ProductCategoryNavBar from "./components/productCategories/ProductCategoryNavBar";
import ProductList from "./components/productCategories/ProductList";

// Referral Coupons
import ReferralCouponCode from "./components/referralCoupons/ReferralCouponCode";
import CopyReferralLinkButton from "./components/referralCoupons/CopyReferralLinkButton";
import ReferralForm from "./components/referralCoupons/ReferralForm";

// Reviews
import StarRating from "./components/reviews/StarRating";
import ReviewVoteButtons from "./components/reviews/ReviewVoteButtons";
import ReportReviewButton from "./components/reviews/ReportReviewButton";
import ReviewForm from "./components/reviews/ReviewForm";
import ReviewPhotoLightbox from "./components/reviews/ReviewPhotoLightbox.vue";
import ReviewVideoPlayer from "./components/reviews/ReviewVideoPlayer.vue";
import ReviewDenialConfirmation from "./components/reviews/ReviewDenialConfirmation.vue";

// Shipments
import RemoveShipmentFromBatchButton from "./components/shipments/RemoveShipmentFromBatchButton";

// Shipment Batches
import BatchShipmentsButton from "./components/shipmentBatches/BatchShipmentsButton";
import DeleteShipmentBatchButton from "./components/shipmentBatches/DeleteShipmentBatchButton";
import EditShipmentBatchShippingMethodButton from "./components/shipmentBatches/EditShipmentBatchShippingMethodButton";
import ShipAllShipmentBatchesButton from "./components/shipmentBatches/ShipAllShipmentBatchesButton";
import ShipShipmentBatchButton from "./components/shipmentBatches/ShipShipmentBatchButton";
import ShipmentBatchSearchForm from "./components/shipmentBatches/ShipmentBatchSearchForm";
import ShipmentBatchStatusButton from "./components/shipmentBatches/ShipmentBatchStatusButton";

// Subscriptions
import SubscriptionSearchForm from "./components/subscriptions/SubscriptionSearchForm";
import ChangeSubscriptionShippingAddressButton from "./components/subscriptions/ChangeSubscriptionShippingAddressButton";
import ChangeSubscriptionPaymentMethodButton from "./components/subscriptions/ChangeSubscriptionPaymentMethodButton";
import ActivateSubscriptionButton from "./components/subscriptions/ActivateSubscriptionButton";
import CancelSubscriptionButton from "./components/subscriptions/CancelSubscriptionButton";
import UpdateSubscriptionNextShipmentDateButton from "./components/subscriptions/UpdateSubscriptionNextShipmentDateButton";
import UpdateSubscriptionHowOftenButton from "./components/subscriptions/UpdateSubscriptionHowOftenButton";
import ChargeSubscriptionButton from "./components/subscriptions/ChargeSubscriptionButton";

const app = createApp({
    components: {
        // Universal
        AOneClick,
        StatusMessage,
        Tooltip,

        // Addresses
        AddressForm,

        // Auth
        LoginForm,
        RegisterForm,
        PasswordResetRequestForm,
        PasswordResetForm,
        LoginLinkRequestForm,

        // Cart
        Cart,

        // Charts
        ElevenzymeClinicalStudyChart,
        RedicalmClinicalStudyChart,
        RediniteClinicalStudyChart,
        RedimindClinicalStudyChart,
        RedimoveClinicalStudyChart,
        ProstacorClinicalStudyChart,
        TestocorClinicalStudyChart,
        ThyrogardClinicalStudyChart,
        HepagardClinicalStudyChart,
        MenovairClinicalStudyChart,
        ThreeBiomeClinicalStudyChart,
        GlucosamineStudyChart,
        HormoneDeclineStudyChart,

        // Checkout
        UserCheckout,
        PaypalCheckout,
        GuestCheckout,
        CheckoutShippingOptionButtons,
        TermsAndConditionsButton,
        PostCheckoutEmailForm,
        PostCheckoutMap,
        HealthProfileSideBar,

        // Contact Us
        CompanyPhoneLink,

        // Dashboard
        PrintMasterCartonLabelButton,
        PrintUspsScanFormButton,
        SalesLeaderboard,
        SalesTotals,

        // Health Profile
        HealthProfileAddToCartButton,

        // Mail Campaigns
        MailCampaigns,

        // Navigation
        NavBar,
        CartSideBar,

        // Orders
        AddressDropdown,
        OrderSearchForm,
        ChangeOrderShippingAddressButton,
        CancelOrderButton,
        FlagOrderAsFraudButton,
        RefundOrderButton,
        RecordChargebackButton,
        ReverseChargebackButton,
        ShipOrderButton,
        MarkOrderAsShippedButton,
        EditOrderItemButton,
        EditOrderShippingTimeButton,
        EditOrderPlacedByUserButton,
        EditAddressFieldButton,
        ShipmentStatusButton,
        CombineOrderButton,
        UncombineOrderButton,
        PreviousOrderSummaryButton,

        // Payment Methods
        PaymentMethodIcon,
        PaymentMethodForm,

        // Products
        BreadcrumbPhoneNumber,
        PhoneNumber,
        ProductVideoModal,
        ProductImageGallery,
        ProductIngredientsBreakdown,
        ProductOrderForm,
        OutOfStockForm,
        ProductQuantityAndSubscriptionOptionButtons,
        RecommendedProductSideBar,
        RedicalmCycleOfNegativeBehavior,
        CycleStages,
        NextCycleStage,
        BuyWithPrimeButton,
        AddToCartButton,

        // Product Categories
        ProductCategoryHeading,
        ProductCategoryNavBar,
        ProductList,

        // Referral Coupons
        ReferralCouponCode,
        CopyReferralLinkButton,
        ReferralForm,

        // Reviews
        StarRating,
        ReviewVoteButtons,
        ReportReviewButton,
        ReviewForm,
        ReviewPhotoLightbox,
        ReviewVideoPlayer,
        ReviewDenialConfirmation,

        // Shipments
        RemoveShipmentFromBatchButton,

        // Shipment Batches
        BatchShipmentsButton,
        DeleteShipmentBatchButton,
        EditShipmentBatchShippingMethodButton,
        ShipAllShipmentBatchesButton,
        ShipShipmentBatchButton,
        ShipmentBatchSearchForm,
        ShipmentBatchStatusButton,

        // Subscriptions
        SubscriptionSearchForm,
        ChangeSubscriptionShippingAddressButton,
        ChangeSubscriptionPaymentMethodButton,
        ActivateSubscriptionButton,
        CancelSubscriptionButton,
        UpdateSubscriptionNextShipmentDateButton,
        UpdateSubscriptionHowOftenButton,
        ChargeSubscriptionButton,
    },

    provide: {
        mitt: emitter,
    },
});

Bugsnag.start({
    apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginVue()],
});

app.component("SvgVue", SvgVue);
app.use(VueSplide);
app.use(VueGtag, {
    property: {
        id: `${import.meta.env.VITE_GOOGLE_ADS_ACCOUNT}/${import.meta.env.VITE_GOOGLE_PHONE_CONVERSION_ID}`,
    },
});
app.use(Bugsnag.getPlugin("vue"));
app.mount("#app");

/**
 * Install the real third-party load callbacks now that the app is mounted and
 * components are subscribed to the mitt bus. Any callback the inline stubs (see
 * layouts/vendor-callbacks.blade.php) recorded before this module ran is
 * replayed so its mitt event still fires.
 */
const installVendorCallback = (name, flag, event) => {
    const alreadyFired =
        window.__vendorCallbacks && window.__vendorCallbacks[flag];

    window[name] = () => emitter.emit(event);

    if (alreadyFired) {
        emitter.emit(event);
    }
};

installVendorCallback("loadApplePay", "applePay", "applePayLoaded");
installVendorCallback("loadSeon", "seon", "seonLoaded");
installVendorCallback("loadGoogleMaps", "googleMaps", "googleMapsLoaded");
